"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  ModalHeader,
  Input,
  FormGroup,
  Form,
  Label,
  Spinner,
  Alert,
  InputGroup,
  InputGroupText,
  Badge,
} from "reactstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import Api from "../../api/Api";

// Form validation schema
const formSchema = yup.object().shape({
  title: yup.string().required("Title is required"),
  titleAr: yup.string().required("Arabic title is required"),
  desc: yup.string().required("Description is required"),
  descAr: yup.string().required("Arabic description is required"),
  video: yup.mixed(),
});

const HotelMap = () => {
  const [mapPoints, setMapPoints] = useState([]);
  const [filteredMapPoints, setFilteredMapPoints] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [currentMapPoint, setCurrentMapPoint] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editItem, setEditItem] = useState({});
  const [videoPreview, setVideoPreview] = useState(null);
  const [showErrors, setShowErrors] = useState(false);

  const videoInputRef = useRef(null);

  // Form setup with Controller
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: "onSubmit",
  });

  // Watch video field for changes
  const watchVideo = watch("video");

  // Fetch map points from API on component mount
  useEffect(() => {
    fetchMapPoints();
  }, []);

  // Update filtered map points when map points or search query changes
  useEffect(() => {
    if (searchQuery) {
      const filtered = mapPoints.filter(
        (point) =>
          point.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          point.titleAr.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredMapPoints(filtered);
    } else {
      setFilteredMapPoints(mapPoints);
    }
  }, [mapPoints, searchQuery]);

  // Handle video file change
  useEffect(() => {
    if (watchVideo && watchVideo[0]) {
      const file = watchVideo[0];
      const videoUrl = URL.createObjectURL(file);
      setVideoPreview(videoUrl);

      return () => {
        URL.revokeObjectURL(videoUrl);
      };
    }
  }, [watchVideo]);

  // Fetch map points from API
  const fetchMapPoints = async () => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const response = await Api.get("/hotelmap");
      if (response.data && response.data.data) {
        setMapPoints(response.data.data);
      } else {
        setMapPoints([]);
      }
    } catch (error) {
      console.error("Error fetching map points:", error);
      setLoadError("Failed to load map points. Please try again later.");
      setMapPoints([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle video upload
  const handleVideoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setValue("video", e.target.files);
    }
  };

  // Remove video preview
  const removeVideoPreview = () => {
    setValue("video", null);
    setVideoPreview(null);
    if (videoInputRef.current) {
      videoInputRef.current.value = "";
    }
  };

  // Send data to Laravel API
  const sendDataToApi = async (data) => {
    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(false);
    setUploadProgress(0);

    try {
      // Create FormData to send files and data
      const formData = new FormData();

      // Add text fields
      formData.append("title", data.title);
      formData.append("titleAr", data.titleAr);
      formData.append("desc", data.desc);
      formData.append("descAr", data.descAr);

      // Add video if exists
      if (data.video && data.video[0]) {
        formData.append("video", data.video[0]);
      }

      // If editing, add the ID
      if (editItem.id) {
        formData.append("id", editItem.id);
        formData.append("_method", "PUT"); // For Laravel method spoofing
      }

      // Determine endpoint based on whether we're editing or creating
      const endpoint = editItem.id ? `/hotelmap/${editItem.id}` : "/hotelmap";
      const method = editItem.id ? "post" : "post"; // Using post with _method for PUT

      // Send the request to your Laravel API
      const response = await Api[method](endpoint, formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      // Handle successful response
      console.log("Upload successful:", response.data);
      setUploadSuccess(true);

      // Update the map points list
      if (editItem.id) {
        // Update existing item
        setMapPoints(
          mapPoints.map((point) =>
            point.id === editItem.id ? response.data.data : point
          )
        );
      } else {
        // Add new item
        setMapPoints([...mapPoints, response.data.data]);
      }

      // Reset form and state
      setTimeout(() => {
        reset();
        setVideoPreview(null);
        setEditItem({});
        setIsAddModalOpen(false);
        setUploadSuccess(false);
        setShowErrors(false);
      }, 1500);
    } catch (error) {
      console.error("Upload error:", error);
      setUploadError(
        error.response?.data?.message ||
          error.message ||
          "Failed to save map point. Please try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  // Handle form submission
  const onSubmit = (data) => {
    setShowErrors(false);
    sendDataToApi(data);
  };

  // Handle form submission error
  const onError = () => {
    setShowErrors(true);
  };

  // Handle search
  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
  };

  // Handle checkbox selection
  const handleCheckboxChange = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Handle select all checkboxes
  const handleSelectAll = () => {
    if (selectedItems.length === currentItems.length) {
      // If all are selected, unselect all
      setSelectedItems([]);
    } else {
      // Otherwise, select all current items
      setSelectedItems(currentItems.map((item) => item.id));
    }
  };

  // Handle edit
  const handleEdit = (item) => {
    setEditItem(item);

    // Set form values
    setValue("title", item.title);
    setValue("titleAr", item.titleAr);
    setValue("desc", item.desc);
    setValue("descAr", item.descAr);

    // Set video preview if available
    if (item.video_url) {
      setVideoPreview(item.video_url);
    } else {
      setVideoPreview(null);
    }

    setIsAddModalOpen(true);
    setShowErrors(false);
  };

  // Handle view
  const handleView = (item) => {
    setCurrentMapPoint(item);
    setIsViewModalOpen(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    try {
      // Call API to delete the map point
      await Api.delete(`/hotelmap/${id}`);

      // Update local state
      setMapPoints(mapPoints.filter((point) => point.id !== id));
      setSelectedItems(selectedItems.filter((item) => item !== id));
    } catch (error) {
      console.error("Error deleting map point:", error);
      // Show error message
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    try {
      // Call API to delete multiple map points
      await Api.post("/hotelmap/bulk-delete", { ids: selectedItems });

      // Update local state
      const updatedMapPoints = mapPoints.filter(
        (point) => !selectedItems.includes(point.id)
      );
      setMapPoints(updatedMapPoints);
      setSelectedItems([]);
    } catch (error) {
      console.error("Error deleting map points:", error);
      // Show error message
    }
  };

  // Toggle add modal
  const toggleAddModal = () => {
    setIsAddModalOpen(!isAddModalOpen);
    if (!isAddModalOpen) {
      // Opening the modal
      reset();
      setEditItem({});
      setVideoPreview(null);
      setShowErrors(false);
    }
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredMapPoints.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredMapPoints.length / itemsPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const setPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbers = 10;
    const maxVisiblePages = Math.min(totalPages, maxPageNumbers);

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  document.title = "Hotel Map | Rosa";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="h3">Hotel Map</h1>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="#">Home</a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Hotel Map
                </li>
              </ol>
            </nav>
          </div>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div id="mapPointsList">
                    <Row className="g-4 mb-3">
                      <Col className="col-sm-auto">
                        <div className="d-flex gap-1">
                          <Button
                            color="success"
                            className="add-btn"
                            onClick={toggleAddModal}
                            id="create-btn"
                          >
                            <i className="ri-add-line align-bottom me-1"></i>{" "}
                            Add Map Point
                          </Button>
                          {selectedItems.length > 0 && (
                            <Button color="danger" onClick={handleBulkDelete}>
                              <i className="ri-delete-bin-line align-bottom me-1"></i>{" "}
                              Delete Selected ({selectedItems.length})
                            </Button>
                          )}
                        </div>
                      </Col>
                      <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end">
                          <div className="search-box ms-2">
                            <InputGroup>
                              <InputGroupText>
                                <i className="ri-search-line"></i>
                              </InputGroupText>
                              <Input
                                type="text"
                                className="form-control search"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={handleSearch}
                              />
                            </InputGroup>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* Loading State */}
                    {isLoading && (
                      <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2">Loading map points...</p>
                      </div>
                    )}

                    {/* Error State */}
                    {loadError && !isLoading && (
                      <Alert color="danger" className="my-3">
                        {loadError}
                      </Alert>
                    )}

                    {/* Select All Checkbox */}
                    {!isLoading && currentItems.length > 0 && (
                      <div className="d-flex align-items-center mb-3">
                        <div className="form-check">
                          <Input
                            type="checkbox"
                            className="form-check-input"
                            id="selectAll"
                            checked={
                              selectedItems.length === currentItems.length &&
                              currentItems.length > 0
                            }
                            onChange={handleSelectAll}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="selectAll"
                          >
                            Select All
                          </label>
                        </div>
                        {selectedItems.length > 0 && (
                          <Badge color="info" className="ms-2">
                            {selectedItems.length} selected
                          </Badge>
                        )}
                      </div>
                    )}

                    {/* Table */}
                    {!isLoading && currentItems.length > 0 && (
                      <div className="table-responsive table-card mt-3 mb-1">
                        <table
                          className="table align-middle table-nowrap"
                          id="mapPointsTable"
                        >
                          <thead className="table-light">
                            <tr>
                              <th scope="col" style={{ width: "50px" }}></th>
                              <th className="sort" data-sort="title">
                                Title
                              </th>
                              <th className="sort" data-sort="titleAr">
                                Arabic Title
                              </th>
                              <th className="sort" data-sort="desc">
                                Description
                              </th>
                              <th className="sort" data-sort="video">
                                Video
                              </th>
                              <th className="sort" data-sort="action">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="list form-check-all">
                            {currentItems.map((point) => (
                              <tr key={point.id}>
                                <td>
                                  <div className="form-check">
                                    <Input
                                      type="checkbox"
                                      className="form-check-input"
                                      value={point.id}
                                      checked={selectedItems.includes(point.id)}
                                      onChange={() =>
                                        handleCheckboxChange(point.id)
                                      }
                                    />
                                  </div>
                                </td>
                                <td className="title">{point.title}</td>
                                <td className="titleAr">{point.titleAr}</td>
                                <td className="desc">
                                  {point.desc.length > 50
                                    ? `${point.desc.substring(0, 50)}...`
                                    : point.desc}
                                </td>
                                <td className="video">
                                  {point.video_url ? (
                                    <Badge color="success">Yes</Badge>
                                  ) : (
                                    <Badge color="light">No</Badge>
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <div className="edit">
                                      <Button
                                        color="success"
                                        size="sm"
                                        className="btn-sm edit-item-btn"
                                        onClick={() => handleEdit(point)}
                                      >
                                        Edit
                                      </Button>
                                    </div>
                                    <div className="view">
                                      <Button
                                        color="info"
                                        size="sm"
                                        className="btn-sm view-item-btn"
                                        onClick={() => handleView(point)}
                                      >
                                        View
                                      </Button>
                                    </div>
                                    <div className="remove">
                                      <Button
                                        color="danger"
                                        size="sm"
                                        className="btn-sm remove-item-btn"
                                        onClick={() => handleDelete(point.id)}
                                      >
                                        Delete
                                      </Button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Empty State */}
                    {!isLoading && currentItems.length === 0 && !loadError && (
                      <div className="text-center py-5">
                        <div className="avatar-md mx-auto mb-4">
                          <div className="avatar-title bg-light rounded-circle text-primary fs-24">
                            <i className="ri-map-pin-line"></i>
                          </div>
                        </div>
                        <h5 className="text-muted mb-3">No map points found</h5>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={toggleAddModal}
                        >
                          Add Map Point
                        </Button>
                      </div>
                    )}

                    {/* Pagination */}
                    {!isLoading && totalPages > 1 && (
                      <div className="d-flex justify-content-center mt-4">
                        <div className="pagination-wrap hstack gap-2">
                          <div className="d-flex justify-content-end">
                            <div
                              className="btn-group me-2"
                              role="group"
                              aria-label="First group"
                            >
                              <button
                                className="page-item pagination-prev btn btn-primary"
                                onClick={prevPage}
                                type="button"
                                disabled={currentPage === 1}
                              >
                                Previous
                              </button>
                              {getPageNumbers().map((pageNumber) => (
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  key={pageNumber}
                                  disabled={pageNumber === currentPage}
                                  onClick={() => setPage(pageNumber)}
                                >
                                  {pageNumber}
                                </button>
                              ))}
                              <button
                                disabled={currentPage === totalPages}
                                className="page-item pagination-prev btn btn-primary"
                                type="button"
                                onClick={nextPage}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Add/Edit Modal */}
          <Modal
            isOpen={isAddModalOpen}
            toggle={toggleAddModal}
            centered
            size="lg"
          >
            <ModalHeader toggle={toggleAddModal}>
              {editItem.id ? "Edit Map Point" : "Add New Map Point"}
            </ModalHeader>
            <Form onSubmit={handleSubmit(onSubmit, onError)}>
              <ModalBody>
                {/* Error and Success Messages */}
                {uploadError && (
                  <Alert color="danger" className="mb-3">
                    {uploadError}
                  </Alert>
                )}

                {uploadSuccess && (
                  <Alert color="success" className="mb-3">
                    Map point saved successfully!
                  </Alert>
                )}

                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="title">Title</Label>
                      <Controller
                        name="title"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="text"
                            id="title"
                            placeholder="Enter title"
                          />
                        )}
                      />
                      {showErrors && errors.title && (
                        <div className="text-danger mt-1">
                          {errors.title.message}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="titleAr">Arabic Title</Label>
                      <Controller
                        name="titleAr"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="text"
                            id="titleAr"
                            placeholder="Enter Arabic title"
                            dir="rtl"
                          />
                        )}
                      />
                      {showErrors && errors.titleAr && (
                        <div className="text-danger mt-1">
                          {errors.titleAr.message}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="desc">Description</Label>
                      <Controller
                        name="desc"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="textarea"
                            id="desc"
                            placeholder="Enter description"
                            rows={4}
                          />
                        )}
                      />
                      {showErrors && errors.desc && (
                        <div className="text-danger mt-1">
                          {errors.desc.message}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="descAr">Arabic Description</Label>
                      <Controller
                        name="descAr"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="textarea"
                            id="descAr"
                            placeholder="Enter Arabic description"
                            rows={4}
                            dir="rtl"
                          />
                        )}
                      />
                      {showErrors && errors.descAr && (
                        <div className="text-danger mt-1">
                          {errors.descAr.message}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup>
                  <Label for="video">Video (Optional)</Label>
                  <div
                    className="border border-dashed rounded p-4 text-center cursor-pointer"
                    style={{ backgroundColor: "#f8f9fa" }}
                    onClick={() => videoInputRef.current?.click()}
                  >
                    {!videoPreview ? (
                      <>
                        <i className="ri-video-upload-line display-4 text-muted"></i>
                        <p className="mt-2">Click to upload a video</p>
                        <p className="small text-muted">
                          MP4, WebM, Ogg up to 50MB
                        </p>
                      </>
                    ) : (
                      <div className="position-relative">
                        <video
                          src={videoPreview}
                          controls
                          className="img-fluid mb-2"
                          style={{ maxHeight: "200px" }}
                        />
                        <Button
                          color="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 rounded-circle p-1"
                          style={{ margin: "2px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeVideoPreview();
                          }}
                          disabled={isUploading}
                        >
                          <i className="ri-close-line"></i>
                        </Button>
                      </div>
                    )}
                    <Controller
                      name="video"
                      control={control}
                      defaultValue=""
                      render={({ field: { onChange, value, ...field } }) => (
                        <Input
                          {...field}
                          type="file"
                          id="video"
                          innerRef={videoInputRef}
                          className="d-none"
                          accept="video/*"
                          onChange={(e) => {
                            onChange(e.target.files);
                            handleVideoChange(e);
                          }}
                        />
                      )}
                    />
                  </div>
                  {showErrors && errors.video && (
                    <div className="text-danger small mt-1">
                      {errors.video.message}
                    </div>
                  )}
                </FormGroup>

                {/* Upload Progress */}
                {isUploading && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span>Upload Progress</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="progress" style={{ height: "20px" }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        style={{ width: `${uploadProgress}%` }}
                        aria-valuenow={uploadProgress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button
                  color="light"
                  onClick={toggleAddModal}
                  disabled={isUploading}
                >
                  Cancel
                </Button>
                <Button color="primary" type="submit" disabled={isUploading}>
                  {isUploading ? (
                    <>
                      <Spinner size="sm" className="me-2" /> Saving...
                    </>
                  ) : (
                    <>{editItem.id ? "Update" : "Save"}</>
                  )}
                </Button>
              </ModalFooter>
            </Form>
          </Modal>

          {/* View Modal */}
          <Modal
            isOpen={isViewModalOpen}
            toggle={() => setIsViewModalOpen(!isViewModalOpen)}
            size="lg"
            centered
          >
            <ModalHeader toggle={() => setIsViewModalOpen(!isViewModalOpen)}>
              Map Point Details
            </ModalHeader>
            <ModalBody>
              {currentMapPoint && (
                <div className="row">
                  <div className="col-md-6">
                    <h5 className="mb-3">English Information</h5>
                    <div className="mb-3">
                      <h6>Title</h6>
                      <p>{currentMapPoint.title}</p>
                    </div>
                    <div className="mb-3">
                      <h6>Description</h6>
                      <p>{currentMapPoint.desc}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h5 className="mb-3">Arabic Information</h5>
                    <div className="mb-3">
                      <h6>Title (Arabic)</h6>
                      <p dir="rtl">{currentMapPoint.titleAr}</p>
                    </div>
                    <div className="mb-3">
                      <h6>Description (Arabic)</h6>
                      <p dir="rtl">{currentMapPoint.descAr}</p>
                    </div>
                  </div>
                  {currentMapPoint.video_url && (
                    <div className="col-12 mt-3">
                      <h5 className="mb-3">Video</h5>
                      <div className="text-center">
                        <video
                          src={currentMapPoint.video_url}
                          controls
                          className="img-fluid"
                          style={{ maxHeight: "300px" }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                color="secondary"
                onClick={() => setIsViewModalOpen(false)}
              >
                Close
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default HotelMap;
